/**
 * Subscribe to #361's `input-form-checked` socket event.
 * Do not use useContentRefresh for this — it throttles refetches to 60s.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import SocketService from '../../../../packages/shared/socket';
import {
  FORM_CHECK_BLOB_KEY,
  FORM_CHECK_PENDING_TIMEOUT_MS,
  patchRecordFormCheck,
  shouldAcceptFormCheck,
  stripRecordFormCheck,
} from '../utils/formCheckDisplay';

export default function useFormCheckUpdates(instanceId, stage, onFormCheck) {
  const onFormCheckRef = useRef(onFormCheck);
  onFormCheckRef.current = onFormCheck;

  useEffect(() => {
    if (!instanceId || !stage) return undefined;

    let socket = null;
    let retryTimer = null;

    const handle = (data) => {
      if (!data || data.instanceId !== instanceId) return;
      if (data.stage !== stage) return;
      if (!data.recordId || !data.formCheck) return;
      onFormCheckRef.current?.({ recordId: data.recordId, formCheck: data.formCheck });
    };

    const attach = () => {
      socket = SocketService.socket;
      if (!socket) return false;
      socket.emit('join-instance', { instanceId, deviceType: 'display' });
      socket.on('input-form-checked', handle);
      return true;
    };

    // Socket may not exist yet on fresh /impactpath loads (connect runs in a
    // later effect). Retry until it appears, then attach once.
    if (!attach()) {
      retryTimer = setInterval(() => {
        if (attach()) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
      }, 300);
    }

    return () => {
      if (retryTimer) clearInterval(retryTimer);
      if (socket) socket.off('input-form-checked', handle);
    };
  }, [instanceId, stage]);
}

export function usePendingFormCheckIds(timeoutMs = FORM_CHECK_PENDING_TIMEOUT_MS) {
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const timersRef = useRef(new Map());

  useEffect(() => () => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  const clearPending = useCallback((recordId) => {
    const timer = timersRef.current.get(recordId);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(recordId);
    setPendingIds((prev) => {
      if (!prev.has(recordId)) return prev;
      const next = new Set(prev);
      next.delete(recordId);
      return next;
    });
  }, []);

  const markPending = useCallback((recordId) => {
    if (!recordId) return;
    const existing = timersRef.current.get(recordId);
    if (existing) clearTimeout(existing);
    setPendingIds((prev) => {
      if (prev.has(recordId)) return prev;
      const next = new Set(prev);
      next.add(recordId);
      return next;
    });
    timersRef.current.set(recordId, setTimeout(() => {
      timersRef.current.delete(recordId);
      setPendingIds((prev) => {
        if (!prev.has(recordId)) return prev;
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
    }, timeoutMs));
  }, [timeoutMs]);

  return { pendingIds, markPending, clearPending };
}

/**
 * Screen-level form-check state: socket patch, pending timeout, edit-clear, dismiss.
 */
export function useFormCheckState({ instanceId, stage, setRecords }) {
  const blobKey = FORM_CHECK_BLOB_KEY[stage];
  const bufferRef = useRef(new Map());
  const ignoreBeforeRef = useRef({});
  const { pendingIds, markPending, clearPending } = usePendingFormCheckIds();
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  useFormCheckUpdates(instanceId, stage, ({ recordId, formCheck }) => {
    const ignoreAt = ignoreBeforeRef.current[recordId];
    if (!shouldAcceptFormCheck(null, formCheck, ignoreAt)) return;
    clearPending(recordId);
    setDismissedIds((prev) => {
      if (!prev.has(recordId)) return prev;
      const next = new Set(prev);
      next.delete(recordId);
      return next;
    });
    let found = false;
    setRecords((prev) => {
      const patched = patchRecordFormCheck(prev, recordId, formCheck, blobKey);
      found = patched.found;
      return patched.records;
    });
    if (!found) bufferRef.current.set(recordId, formCheck);
  });

  const noteCreated = useCallback((record) => {
    if (!record?.id) return record;
    const buffered = bufferRef.current.get(record.id);
    if (buffered) {
      bufferRef.current.delete(record.id);
      return { ...record, [blobKey]: { ...(record[blobKey] || {}), formCheck: buffered } };
    }
    if (!record[blobKey]?.formCheck) markPending(record.id);
    return record;
  }, [blobKey, markPending]);

  const noteEdited = useCallback((recordId) => {
    if (!recordId) return;
    ignoreBeforeRef.current[recordId] = Date.now();
    bufferRef.current.delete(recordId);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(recordId);
      return next;
    });
    setRecords((prev) => prev.map((record) => (
      String(record.id) === String(recordId)
        ? stripRecordFormCheck(record, blobKey)
        : record
    )));
    // Show pending shimmer while the re-check runs (create already marks
    // pending; edit previously left the old warn frozen).
    markPending(recordId);
  }, [blobKey, markPending, setRecords]);

  const dismiss = useCallback((recordId) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(recordId);
      return next;
    });
  }, []);

  return { pendingIds, dismissedIds, markPending, noteCreated, noteEdited, dismiss };
}
